/**
 * Settings tabs: order, URL slugs and the legacy slugs that still resolve.
 * The URL (/settings/:tab) is the source of truth for the active tab.
 */
export type SettingsTabKey =
  | 'profile'
  | 'account'
  | 'preferences'
  | 'notifications'
  | 'privacy'
  | 'blocked'
  | 'emoji'
  | 'partners'
  | 'billing';

/** Display order of the top tab bar. The key is also the canonical slug. */
export const SETTINGS_TABS: { key: SettingsTabKey; label: string }[] = [
  { key: 'profile', label: 'Profile' },
  { key: 'account', label: 'Account' },
  { key: 'preferences', label: 'Preferences' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'privacy', label: 'Privacy' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'emoji', label: 'Emoji' },
  { key: 'partners', label: 'Partners' },
  { key: 'billing', label: 'Billing' },
];

export const DEFAULT_SETTINGS_TAB: SettingsTabKey = 'profile';

/**
 * Slugs from before the redesign, redirected so existing deep links and
 * bookmarks keep working. `profile` is not here: it used to be an alias of
 * Account and is now the Profile tab's own slug (ruled 2026-10-05).
 */
export const LEGACY_SETTINGS_SLUGS: Record<string, SettingsTabKey> = {
  'public-profile': 'profile',
  public: 'profile',
  email: 'notifications',
  'blocked-users': 'blocked',
  'custom-emoji': 'emoji',
  affiliates: 'partners',
};

/**
 * The tab names in-app callers pass through `navigate('/settings', { state: { tab } })`.
 * They keep their OLD meaning ('Profile' was the Account tab's key), so no caller breaks.
 */
export const LEGACY_STATE_TABS: Record<string, SettingsTabKey> = {
  Profile: 'account',
  Public: 'profile',
  Affiliates: 'partners',
  Preferences: 'preferences',
  Privacy: 'privacy',
  Blocked: 'blocked',
  Email: 'notifications',
  Emoji: 'emoji',
  Billing: 'billing',
};

const isTabKey = (value: string): value is SettingsTabKey => SETTINGS_TABS.some((t) => t.key === value);

export interface ResolvedSettingsTab {
  tab: SettingsTabKey;
  /** Set when the URL should be replaced with the canonical slug (legacy slug, or state-only navigation). */
  redirectTo?: string;
}

/** Which tab a URL slug and/or legacy navigation state means. Unknown slugs fall back to the default tab. */
export const resolveSettingsTab = (
  slug: string | undefined,
  stateTab?: string | undefined,
): ResolvedSettingsTab => {
  const normalized = slug?.toLowerCase();
  if (normalized) {
    if (isTabKey(normalized)) return { tab: normalized };
    const legacy = LEGACY_SETTINGS_SLUGS[normalized];
    if (legacy) return { tab: legacy, redirectTo: `/settings/${legacy}` };
    return { tab: DEFAULT_SETTINGS_TAB };
  }
  const fromState = stateTab ? LEGACY_STATE_TABS[stateTab] : undefined;
  if (fromState) return { tab: fromState };
  return { tab: DEFAULT_SETTINGS_TAB };
};
